import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api.config';
import './DataExplorer.css';

// ── Utility: format cell values for display ───────────────────────
function formatCell(val) {
    if (val === null || val === undefined) return <span className="de-null">null</span>;
    if (typeof val === 'boolean') return <span className={`de-bool de-bool--${val}`}>{String(val)}</span>;
    if (typeof val === 'object') {
        const str = JSON.stringify(val, null, 2);
        return (
            <span className="de-json" title={str}>
                {str.slice(0, 60)}{str.length > 60 ? '…' : ''}
            </span>
        );
    }
    const str = String(val);
    // ISO date
    if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
        return new Date(str).toLocaleString();
    }
    return str;
}

// ── Utility: export to CSV ────────────────────────────────────────
function exportCsv(columns, rows, filename = 'data-explorer.csv') {
    const header = columns.join(',');
    const body = rows.map(row =>
        columns.map(col => {
            const v = row[col];
            if (v === null || v === undefined) return '';
            const str = typeof v === 'object' ? JSON.stringify(v) : String(v);
            return `"${str.replace(/"/g, '""')}"`;
        }).join(',')
    ).join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// ── Sort/filter hook ──────────────────────────────────────────────
function useTableState(rows, columns) {
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [visibleCols, setVisibleCols] = useState(() => new Set(columns));

    // Reset when data changes
    useEffect(() => {
        setPage(0);
        setVisibleCols(new Set(columns));
    }, [JSON.stringify(columns)]);

    const toggleCol = useCallback((col) => {
        setVisibleCols(prev => {
            const next = new Set(prev);
            next.has(col) ? next.delete(col) : next.add(col);
            return next;
        });
    }, []);

    const setFilter = useCallback((col, val) => {
        setFilters(prev => ({ ...prev, [col]: val }));
        setPage(0);
    }, []);

    const handleSort = useCallback((col) => {
        setSortCol(prev => {
            if (prev === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return col; }
            setSortDir('asc');
            return col;
        });
    }, []);

    const processed = React.useMemo(() => {
        let data = [...rows];
        // Filter
        for (const [col, val] of Object.entries(filters)) {
            if (!val) continue;
            const lower = val.toLowerCase();
            data = data.filter(row => {
                const cell = row[col];
                if (cell === null || cell === undefined) return false;
                return String(typeof cell === 'object' ? JSON.stringify(cell) : cell)
                    .toLowerCase().includes(lower);
            });
        }
        // Sort
        if (sortCol) {
            data.sort((a, b) => {
                const av = a[sortCol], bv = b[sortCol];
                if (av === null || av === undefined) return 1;
                if (bv === null || bv === undefined) return -1;
                const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
                return sortDir === 'asc' ? cmp : -cmp;
            });
        }
        return data;
    }, [rows, filters, sortCol, sortDir]);

    const totalPages = Math.ceil(processed.length / pageSize);
    const paged = processed.slice(page * pageSize, (page + 1) * pageSize);

    return {
        processed, paged, totalPages, page, pageSize, setPage, setPageSize,
        sortCol, sortDir, handleSort,
        filters, setFilter,
        visibleCols, toggleCol,
    };
}

// ─────────────────────────────────────────────────────────────────
export default function DataExplorer() {
    const [question, setQuestion]         = useState('');
    const [loading, setLoading]           = useState(false);
    const [result, setResult]             = useState(null);   // last query result
    const [error, setError]               = useState(null);
    const [suggestions, setSuggestions]   = useState([]);
    const [history, setHistory]           = useState([]);     // conversation history for follow-ups
    const [showSql, setShowSql]           = useState(false);
    const [editSql, setEditSql]           = useState('');
    const [showColPicker, setShowColPicker] = useState(false);
    const inputRef = useRef(null);

    const authHeaders = () => {
        const token = localStorage.getItem('authToken');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    };

    // Load suggestions on mount
    useEffect(() => {
        fetch(`${API_BASE_URL}/data-explorer/suggestions`, {
            headers: authHeaders(),
        })
            .then(r => r.json())
            .then(d => d.success && setSuggestions(d.suggestions))
            .catch(() => {});
    }, []);

    const runQuestion = useCallback(async (q, convHistory = history) => {
        if (!q.trim()) return;
        setLoading(true);
        setError(null);
        setResult(null);
        setShowSql(false);

        try {
            const res = await fetch(`${API_BASE_URL}/data-explorer/query`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ question: q, history: convHistory }),
            });
            const data = await res.json();

            if (data.success) {
                setResult(data);
                setEditSql(data.sql || '');
                // Append to conversation history for follow-ups
                setHistory(prev => [
                    ...prev,
                    { role: 'user', content: q },
                    { role: 'assistant', content: `Generated SQL: ${data.sql}` },
                ].slice(-10)); // keep last 5 turns
            } else {
                setError(data.message || 'Query failed');
                if (data.sql) setEditSql(data.sql);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [history]);

    const runEditedSql = useCallback(async () => {
        if (!editSql.trim()) return;
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${API_BASE_URL}/data-explorer/execute`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ sql: editSql }),
            });
            const data = await res.json();
            if (data.success) {
                setResult(prev => ({ ...prev, ...data }));
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [editSql]);

    const handleSubmit = (e) => {
        e.preventDefault();
        runQuestion(question);
    };

    const handleSuggestion = (s) => {
        setQuestion(s.question);
        runQuestion(s.question, []);
        setHistory([]);
        inputRef.current?.focus();
    };

    const clearAll = () => {
        setQuestion('');
        setResult(null);
        setError(null);
        setHistory([]);
        setEditSql('');
    };

    const columns = result?.columns || [];
    const rows    = result?.rows    || [];

    const table = useTableState(rows, columns);

    return (
        <div className="de-root">
            {/* ── Header ── */}
            <div className="de-header">
                <div>
                    <h1 className="de-title">Data Explorer</h1>
                    <p className="de-subtitle">
                        Ask questions about your EPCIS data in plain English. Powered by Claude AI.
                    </p>
                </div>
                {result && (
                    <button className="de-btn de-btn--ghost" onClick={clearAll}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        New Query
                    </button>
                )}
            </div>

            {/* ── Query Input ── */}
            <div className="de-query-card">
                <form onSubmit={handleSubmit} className="de-query-form">
                    <div className="de-query-input-wrap">
                        <svg className="de-query-icon" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <input
                            ref={inputRef}
                            className="de-query-input"
                            value={question}
                            onChange={e => setQuestion(e.target.value)}
                            placeholder="Ask anything… e.g. &quot;Show me all blocked transmissions in the last 7 days&quot;"
                            disabled={loading}
                            autoComplete="off"
                        />
                        {question && (
                            <button type="button" className="de-query-clear" onClick={() => setQuestion('')}>✕</button>
                        )}
                    </div>
                    <button type="submit" className="de-btn de-btn--primary" disabled={loading || !question.trim()}>
                        {loading ? (
                            <span className="de-spinner" />
                        ) : (
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        )}
                        {loading ? 'Thinking…' : 'Run'}
                    </button>
                </form>

                {/* Follow-up hint */}
                {result && history.length > 0 && (
                    <div className="de-followup-hint">
                        <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Context is remembered — ask a follow-up question to refine these results.
                    </div>
                )}
            </div>

            {/* ── Error ── */}
            {error && (
                <div className="de-alert de-alert--error">
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                    <button className="de-alert__close" onClick={() => setError(null)}>✕</button>
                </div>
            )}

            {/* ── Results ── */}
            {result ? (
                <div className="de-results">
                    {/* Result meta bar */}
                    <div className="de-result-meta">
                        <div className="de-result-meta__left">
                            <span className="de-result-description">{result.description}</span>
                            <span className="de-result-count">
                                {table.processed.length.toLocaleString()} row{table.processed.length !== 1 ? 's' : ''}
                                {result.rowCount !== table.processed.length && ` (${result.rowCount} returned, filtered to ${table.processed.length})`}
                            </span>
                            <span className="de-result-timing">{result.durationMs}ms</span>
                        </div>
                        <div className="de-result-meta__right">
                            <button
                                className={`de-btn de-btn--sm de-btn--ghost ${showColPicker ? 'active' : ''}`}
                                onClick={() => setShowColPicker(p => !p)}
                                title="Choose columns"
                            >
                                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                                </svg>
                                Columns
                            </button>
                            <button
                                className={`de-btn de-btn--sm de-btn--ghost ${showSql ? 'active' : ''}`}
                                onClick={() => setShowSql(p => !p)}
                                title="View / edit SQL"
                            >
                                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                </svg>
                                SQL
                            </button>
                            <button
                                className="de-btn de-btn--sm de-btn--ghost"
                                onClick={() => exportCsv(
                                    columns.filter(c => table.visibleCols.has(c)),
                                    table.processed
                                )}
                                title="Export to CSV"
                            >
                                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Export CSV
                            </button>
                        </div>
                    </div>

                    {/* Column picker dropdown */}
                    {showColPicker && (
                        <div className="de-col-picker">
                            <div className="de-col-picker__title">Toggle columns</div>
                            <div className="de-col-picker__grid">
                                {columns.map(col => (
                                    <label key={col} className="de-col-toggle">
                                        <input
                                            type="checkbox"
                                            checked={table.visibleCols.has(col)}
                                            onChange={() => table.toggleCol(col)}
                                        />
                                        <span>{col}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* SQL editor */}
                    {showSql && (
                        <div className="de-sql-editor">
                            <div className="de-sql-editor__label">Generated SQL — edit and re-run if needed</div>
                            <textarea
                                className="de-sql-textarea"
                                value={editSql}
                                onChange={e => setEditSql(e.target.value)}
                                rows={6}
                                spellCheck={false}
                            />
                            <div className="de-sql-editor__actions">
                                <span className="de-guardrail-note">
                                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                    SELECT only · Max 1000 rows · Org-scoped
                                </span>
                                <button className="de-btn de-btn--sm de-btn--primary" onClick={runEditedSql} disabled={loading}>
                                    Re-run SQL
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Data table */}
                    {rows.length === 0 ? (
                        <div className="de-empty-results">No rows returned.</div>
                    ) : (
                        <div className="de-table-wrap">
                            <table className="de-table">
                                <thead>
                                    <tr>
                                        {columns.filter(c => table.visibleCols.has(c)).map(col => (
                                            <th key={col}>
                                                <div className="de-th-inner">
                                                    <button className="de-sort-btn" onClick={() => table.handleSort(col)}>
                                                        {col}
                                                        {table.sortCol === col && (
                                                            <span className="de-sort-arrow">{table.sortDir === 'asc' ? '↑' : '↓'}</span>
                                                        )}
                                                    </button>
                                                    <input
                                                        className="de-col-filter"
                                                        placeholder="filter…"
                                                        value={table.filters[col] || ''}
                                                        onChange={e => table.setFilter(col, e.target.value)}
                                                    />
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {table.paged.map((row, i) => (
                                        <tr key={i}>
                                            {columns.filter(c => table.visibleCols.has(c)).map(col => (
                                                <td key={col}>{formatCell(row[col])}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {table.totalPages > 1 && (
                        <div className="de-pagination">
                            <div className="de-pagination__info">
                                Showing {table.page * table.pageSize + 1}–{Math.min((table.page + 1) * table.pageSize, table.processed.length)} of {table.processed.length}
                            </div>
                            <div className="de-pagination__controls">
                                <button
                                    className="de-btn de-btn--sm de-btn--ghost"
                                    disabled={table.page === 0}
                                    onClick={() => table.setPage(0)}
                                >«</button>
                                <button
                                    className="de-btn de-btn--sm de-btn--ghost"
                                    disabled={table.page === 0}
                                    onClick={() => table.setPage(p => p - 1)}
                                >‹</button>
                                <span className="de-pagination__page">Page {table.page + 1} / {table.totalPages}</span>
                                <button
                                    className="de-btn de-btn--sm de-btn--ghost"
                                    disabled={table.page >= table.totalPages - 1}
                                    onClick={() => table.setPage(p => p + 1)}
                                >›</button>
                                <button
                                    className="de-btn de-btn--sm de-btn--ghost"
                                    disabled={table.page >= table.totalPages - 1}
                                    onClick={() => table.setPage(table.totalPages - 1)}
                                >»</button>
                                <select
                                    className="de-page-size"
                                    value={table.pageSize}
                                    onChange={e => { table.setPageSize(Number(e.target.value)); table.setPage(0); }}
                                >
                                    {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                /* ── Empty state: suggestions ── */
                !loading && (
                    <div className="de-suggestions">
                        <div className="de-suggestions__title">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Quick start — click any question
                        </div>
                        <div className="de-suggestions__grid">
                            {suggestions.map((s, i) => (
                                <button key={i} className="de-suggestion-card" onClick={() => handleSuggestion(s)}>
                                    <span className="de-suggestion-label">{s.label}</span>
                                    <span className="de-suggestion-q">{s.question}</span>
                                </button>
                            ))}
                        </div>
                        <div className="de-guardrails">
                            <div className="de-guardrails__title">Guardrails active</div>
                            <div className="de-guardrails__items">
                                <span>✓ SELECT only — no mutations</span>
                                <span>✓ Max 1,000 rows per query</span>
                                <span>✓ LIMIT required</span>
                                <span>✓ Org-scoped data</span>
                                <span>✓ Approved tables only</span>
                            </div>
                        </div>
                    </div>
                )
            )}

            {/* Loading skeleton */}
            {loading && !result && (
                <div className="de-loading">
                    <div className="de-loading__bar" />
                    <div className="de-loading__bar de-loading__bar--short" />
                    <div className="de-loading__bar de-loading__bar--med" />
                    <p className="de-loading__text">Generating query and fetching data…</p>
                </div>
            )}
        </div>
    );
}
